import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Check, ChevronsUpDown, Factory, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ewbDocsApi } from '@/services/ewayBillApi';
import { slug } from '@/components/ewayBill/TallyNames';

const MAX = 100;
const jobberNote = (j) => {
  if (j.no_pin) return ['no PIN — fix it in Jobber Master', 'text-amber-700'];
  return [[j.gstin === 'URP' ? 'unregistered' : j.gstin, j.place, j.pincode && `PIN ${j.pincode}`].filter(Boolean).join(' · '), 'text-gray-500'];
};

const Opt = ({ on, onClick, icon, title, note, noteCls, testId }) => (
  <button type="button" onClick={onClick} className="w-full flex items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent" data-testid={testId}>
    <Check className={cn('mt-0.5 w-4 h-4 shrink-0', on ? 'opacity-100' : 'opacity-0')} />{icon}
    <span className="min-w-0"><span className="block truncate">{title}</span><span className={`block truncate text-[11px] ${noteCls}`}>{note}</span></span>
  </button>
);

/** Within-site Transfer 'To': our warehouses (Sender units) or an active Jobber Master jobber (GSTIN / PIN from the master). */
export const DestinationPicker = ({ toSection, unitId, jobber, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [d, setD] = useState(null);
  useEffect(() => { ewbDocsApi.destinations(toSection).then(setD).catch(() => setD({ units: [], jobbers: [] })); }, [toSection]);
  const t = q.trim().toLowerCase();
  const units = useMemo(() => (d?.units || []).filter((u) => `${u.name} ${u.place} ${u.pincode}`.toLowerCase().includes(t)), [d, t]);
  const jobbers = useMemo(() => (d?.jobbers || []).filter((j) => `${j.name} ${j.place || ''} ${j.pincode || ''}`.toLowerCase().includes(t)).slice(0, MAX), [d, t]);
  const label = jobber || (d?.units || []).find((u) => u.id === unitId)?.name;
  const pick = (v) => { onChange(v); setOpen(false); setQ(''); };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(''); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className={cn('h-8 max-w-[260px] justify-between text-xs font-normal', !label && 'text-muted-foreground')} data-testid="ewb-dest-select">
          <span className="truncate">{label || 'Pick destination'}</span><ChevronsUpDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="end">
        <div className="flex items-center gap-2 border-b px-3">
          <Search className="w-4 h-4 shrink-0 opacity-50" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search warehouse or jobber…" className="h-10 flex-1 bg-transparent text-sm outline-none" data-testid="ewb-dest-search" />
        </div>
        <div className="max-h-80 overflow-y-auto p-1" data-testid="ewb-dest-options">
          <div className="px-2 pt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Our warehouses</div>
          {units.length === 0 && <p className="px-2 py-1 text-xs text-gray-400">None — add them in Sender units</p>}
          {units.map((u) => (
            <Opt key={u.id} on={!jobber && u.id === unitId} onClick={() => pick({ to_unit_id: u.id, to_jobber: null })} icon={<Building2 className="mt-0.5 w-4 h-4 shrink-0 text-[#2D5F4F]" />}
              title={u.name} note={`${u.place} · PIN ${u.pincode} · ${u.gstin}`} noteCls="text-gray-500" testId={`ewb-dest-unit-${u.id}`} />
          ))}
          <div className="px-2 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Jobbers / processing units</div>
          {jobbers.length === 0 && <p className="px-2 py-1 text-xs text-gray-400">No active jobber found — add them in Jobber Master</p>}
          {jobbers.map((j) => {
            const [note, cls] = jobberNote(j);
            return <Opt key={j.name} on={j.name === jobber} onClick={() => pick({ to_unit_id: null, to_jobber: j.name })} icon={<Factory className="mt-0.5 w-4 h-4 shrink-0 text-amber-700" />}
              title={j.name} note={note} noteCls={cls} testId={`ewb-dest-jobber-${slug(j.name)}`} />;
          })}
          {jobbers.length === MAX && <p className="px-2 py-1 text-xs text-gray-400">Showing first {MAX} jobbers — type to narrow down.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DestinationPicker;
